import classNames from 'classnames'
import ordinal from 'ordinal'
import type {ReactElement} from 'react'
import {useMemo} from 'react'

import {useIsPipelinesSelected} from '../../../hooks/routes'
import {useDistanceToNow} from '../../../hooks/useTimeSelector'
import {getBuild} from '../../../selectors'
import type {BuildId} from '../../../types'
import AgentInfo from '../AgentInfo/AgentInfo.container'
import ApprovalInfo from '../BuildApproval/BuildApproval.container'
import {BuildApprovalViewMode} from '../BuildApproval/BuildApproval.types'
import BuildPathAndStatus from '../BuildPathAndStatus/BuildPathAndStatus'
import BuildQueueInfo from '../BuildQueueInfo/BuildQueueInfo.container'
import BuildTimeInfo from '../BuildTimeInfo/BuildTimeInfo.container'
import BuildTriggeredBy from '../BuildTriggeredBy/BuildTriggeredBy.container'
import CompatibleAgents from '../CompatibleAgents/CompatibleAgents.container'
import SvgIcon from '../SvgIcon/SvgIcon'
import Visible from '../Visible/Visible'

import BuildPartOf from './BuildPartOf/BuildPartOf'
import SnapshotDependenciesCounters from './SnapshotDependenciesCounters/SnapshotDependenciesCounters.container'

import styles from './BuildInfoTable.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildId: BuildId
  className?: string
}

function BuildInfoTable({buildId, className}: Props): ReactElement {
  const queued = useAppSelector(state => getBuild(state, buildId)?.state === 'queued')
  const queueInfo = useMemo(
    () => (
      <span className={styles.label}>
        <SvgIcon icon="hourglass" className={styles.queuedIcon} />
        <BuildQueueInfo titleClassName={styles.queueInfoTitle} buildId={buildId} />
      </span>
    ),
    [buildId],
  )
  const startEstimate = useAppSelector(state => getBuild(state, buildId)?.startEstimate)
  const timeToStart = useDistanceToNow(startEstimate, false)
  const waitReason = useAppSelector(state => getBuild(state, buildId)?.waitReason)
  const queuePosition = useAppSelector(state => getBuild(state, buildId)?.queuePosition)
  const composite = useAppSelector(state => getBuild(state, buildId)?.composite)
  const delayedByBuildId = useAppSelector(state => getBuild(state, buildId)?.delayedByBuild)
  const isPipelines = useIsPipelinesSelected()

  return (
    <Visible>
      {() => (
        <table className={classNames(styles.info, className)}>
          <tbody>
            <BuildTimeInfo buildId={buildId}>{queueInfo}</BuildTimeInfo>
            {queued && (
              <>
                <tr>
                  <th>{'To\u00a0start'}</th>
                  <td className={styles.timeToStartAndWaitReason}>
                    <span className={styles.timeToStart}>{timeToStart ?? 'Unknown'}</span>
                    {waitReason != null && (
                      <>
                        {', '}
                        <span className={styles.waitReason}>{waitReason[0]}</span>
                        <span>{waitReason.slice(1)}</span>
                      </>
                    )}
                    {queuePosition != null && (
                      <span className={styles.label}>
                        <SvgIcon icon="hourglass" className={styles.queuedIcon} />
                        {ordinal(queuePosition)}
                        {' position in queue, waiting '}
                        <BuildQueueInfo buildId={buildId} withoutTitle />
                      </span>
                    )}
                    {!composite && (
                      <>
                        <span className={styles.label}>{'Can run on'}</span>
                        <CompatibleAgents className={styles.compatibleAgents} buildId={buildId} />
                      </>
                    )}
                  </td>
                </tr>
              </>
            )}
            <AgentInfo buildId={buildId} secondary={false} withLabel />
            {delayedByBuildId && (
              <tr>
                <th>{'Delayed\u00a0by'}</th>
                <td>
                  <BuildPathAndStatus buildId={delayedByBuildId} />
                </td>
              </tr>
            )}
            {!isPipelines && (
              <BuildTriggeredBy inTable buildId={buildId} inline={false} withFullDate />
            )}
            <ApprovalInfo view={BuildApprovalViewMode.INLINE} buildId={buildId} />
            {!isPipelines && <BuildPartOf buildId={buildId} />}
            {!isPipelines && <SnapshotDependenciesCounters buildId={buildId} />}
          </tbody>
        </table>
      )}
    </Visible>
  )
}

export default BuildInfoTable
